/**
 * main.ts — circular-ruin composition root.
 *
 * Phase 4:
 *   Wires AudioEngine + VisualEngine + InputLayer + MachineMapper + SessionManager
 *   into the spec §5 rAF loop so a real iPhone can play the gesture-mapped
 *   feedback machine.
 *
 * Constraints:
 *   - Never imports DOM-touching libraries beyond what engines/UX wrap.
 *   - Audio context is captured from Tone; unmuteIOSAudio() + Tone.start() both
 *     start synchronously inside the first user gesture so iOS silent-switch +
 *     autoplay both unlock.
 */

import * as Tone from 'tone';

import { unmuteIOSAudio } from './util/unmute-ios-audio';
import { shouldOpenInstrumentGate } from './util/instrument-gate';
import { loadModes } from './modes';
import { AudioEngine } from './components/AudioEngine';
import { VisualEngine } from './components/VisualEngine';
import { InputLayer } from './components/InputLayer';
import { MachineMapper } from './components/MachineMapper';
import { SessionManager } from './components/SessionManager';
import type {
  MachineMapperOutput,
  ModeConfig,
  GestureFrame,
  StateName,
  VisualParams,
  AudioLevels,
} from './types';

// ─── Constants ──────────────────────────────────────────────────────────────

const IDLE_EVOLVE_MS = 8000; // spec §7 row 10
const TEST_FAST =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('test-fast');
const IDLE_EVOLVE_MS_EFFECTIVE = TEST_FAST ? 800 : IDLE_EVOLVE_MS;

const DEBUG =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');
const E2E =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('e2e');

// ─── DOM resolution ─────────────────────────────────────────────────────────

interface DomRefs {
  canvas: HTMLCanvasElement;
  splash: HTMLElement;
  silentAudio: HTMLAudioElement;
  hapticTap: HTMLLabelElement | null;
  hapticHard: HTMLLabelElement | null;
}

function resolveDom(): DomRefs {
  const canvas = document.getElementById('stage');
  const splash = document.getElementById('tap-to-start');
  const silentAudio = document.getElementById('silent');
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error('main: #stage canvas missing');
  if (!(splash instanceof HTMLElement)) throw new Error('main: #tap-to-start splash missing');
  if (!(silentAudio instanceof HTMLAudioElement))
    throw new Error('main: #silent audio element missing');
  return {
    canvas,
    splash,
    silentAudio,
    hapticTap: document.getElementById('haptic-tap') as HTMLLabelElement | null,
    hapticHard: document.getElementById('haptic-hard') as HTMLLabelElement | null,
  };
}

// ─── Canvas sizing ──────────────────────────────────────────────────────────

interface CanvasRenderSize {
  width: number;
  height: number;
}

function getCanvasRenderSize(): CanvasRenderSize {
  // E2E mode: tiny fixed canvas. Headless Chromium mobile emulation reports
  // outrageous innerWidth/DPR combinations that produce 10000+ px canvases,
  // exceed GL_MAX_RENDERBUFFER_SIZE, and freeze the rAF loop. The E2E test
  // only cares about state transitions, not visual fidelity, so we use a small
  // predictable size.
  if (E2E) {
    return { width: 256, height: 256 };
  }
  // Let three.js own DPR via setPixelRatio(). This returns CSS-pixel render
  // dimensions, optionally downscaled so the final drawing buffer stays bounded.
  const MAX_SIDE = 2048;
  const rawDpr = window.devicePixelRatio ?? 1;
  const dpr = Math.min(Math.max(rawDpr, 1), 3);
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const scale = Math.min(1, MAX_SIDE / Math.max(viewportW * dpr, viewportH * dpr));
  return {
    width: Math.max(1, Math.floor(viewportW * scale)),
    height: Math.max(1, Math.floor(viewportH * scale)),
  };
}

// ─── Haptics ────────────────────────────────────────────────────────────────

function fireHaptic(refs: DomRefs, flavor: 'tap' | 'hard'): void {
  const el = flavor === 'hard' ? refs.hapticHard : refs.hapticTap;
  el?.click();
}

// ─── Body state attribute ───────────────────────────────────────────────────

function reflectState(state: StateName, refs: DomRefs): void {
  document.body.dataset.state = state;
  if (state === 'AWAITING_TAP' || state === 'LOADING') {
    refs.splash.style.display = 'flex';
  } else {
    refs.splash.style.display = 'none';
  }
}

// ─── Debug HUD ──────────────────────────────────────────────────────────────

/** Append-only diagnostics panel visible when ?debug=1. Updated each frame. */
function makeDebugHud(): HTMLDivElement {
  const el = document.createElement('div');
  el.id = 'debug-hud';
  el.style.cssText = [
    'position:fixed', 'top:8px', 'left:8px', 'z-index:100',
    'background:rgba(0,0,0,0.7)', 'color:#0f0', 'font:11px/1.4 monospace',
    'padding:8px', 'border:1px solid #0f0', 'border-radius:4px',
    'max-width:60vw', 'pointer-events:none', 'white-space:pre',
  ].join(';');
  el.textContent = 'debug hud: initialising…';
  document.body.appendChild(el);
  return el;
}

// ─── Activity peek ──────────────────────────────────────────────────────────

/** Cheap "did anything happen this frame" check derived from the flushed gesture. */
function gestureHadActivity(g: GestureFrame): boolean {
  return (
    g.fingers.size > 0 ||
    g.taps.length > 0 ||
    g.doubleTaps.length > 0 ||
    g.holds.length > 0 ||
    g.pinch !== null ||
    g.rotate !== null
  );
}

// ─── Boot ───────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  const refs = resolveDom();
  const initialCanvasSize = getCanvasRenderSize();

  const hud = DEBUG ? makeDebugHud() : null;
  let frameCount = 0;
  let lastHudAtMs = 0;

  // 1. Modes — must load before VisualEngine (needs modes upfront).
  const modesFile = await loadModes('/media/modes.json');
  let activeModeIndex: 0 | 1 | 2 = 0;
  let activeMode: ModeConfig = modesFile.modes[0];

  // 2. Engines. AudioEngine loads its own modes via modesUrl; VisualEngine accepts
  //    the loaded array.
  const audioEngine = new AudioEngine();
  const visualEngine = new VisualEngine({
    canvas: refs.canvas,
    modes: modesFile.modes,
    size: initialCanvasSize,
  });
  window.addEventListener('resize', () => {
    const size = getCanvasRenderSize();
    visualEngine.resize(size.width, size.height);
  }, { passive: true });
  const inputLayer = new InputLayer({ target: refs.canvas });
  inputLayer.attach();

  // 3. Tone provides our AudioContext. Suspended on iOS until Tone.start() inside
  //    the first user gesture — SessionManager.handleTap drives that.
  const audioContext = Tone.getContext().rawContext as unknown as AudioContext;

  // 4. UX brain.
  const machineMapper = new MachineMapper({
    seed: Math.floor(Date.now() % 100000),
    idleEvolveMs: IDLE_EVOLVE_MS_EFFECTIVE,
    barMs: (60_000 / activeMode.tempo) * 4,
  });

  const session = new SessionManager({
    audioContext,
    unlockAudio: E2E
      ? // E2E test environment: headless Chromium AudioContext.resume() hangs
        // without an audio device. Bypass the real unmute and let SessionManager
        // transition to PLAYING immediately. Audio is verified by unit tests.
        async () => {}
      : async () => {
          // CRITICAL iOS ordering: both unmute paths must START synchronously inside
          // the user gesture, BEFORE any await. Then we await the resulting promises.
          const silentPromise = unmuteIOSAudio(refs.silentAudio);
          const tonePromise = Tone.start();
          await Promise.all([silentPromise, tonePromise]);
        },
    now: () => performance.now(),
  });

  // 5. Eagerly preload mode 0 on both engines, then wire up the video texture
  //    via setMode(0) — loadMode creates the VideoSource but setMode is what
  //    actually points uVideo at the texture. Without this the shader samples
  //    a null texture and stays black on black forever.
  await Promise.all([audioEngine.loadMode(0), visualEngine.loadMode(0)]);
  audioEngine.setMode(0);
  visualEngine.setMode(0);
  // Start the (muted, playsinline) video immediately — iOS allows muted
  // autoplay without a gesture, so the footage is already moving behind the
  // "tap for sound" splash. Audio still waits for the tap (browser rule).
  void visualEngine.playLoadedVideos().catch((err) => console.warn('boot video play', err));
  session.markReady();

  // 6. State change → reflect to DOM.
  session.on({
    onStateChange: (state, prev) => {
      reflectState(state, refs);
      if (state !== 'PLAYING') audioEngine.setInstrumentGate(false);
      if (DEBUG) console.log(`[state] ${prev} → ${state}`);
    },
  });
  reflectState(session.state, refs); // initial sync (LOADING for an instant, AWAITING_TAP after markReady)

  // 7. Tap-to-start.
  let bgLoadStarted = false;
  refs.splash.addEventListener('pointerdown', () => {
    // EVERY audio/video unlock path must START synchronously inside this user
    // gesture. Chrome and iOS Safari both invalidate the gesture token after
    // the first await, so we kick off all the promises here and only await
    // them later inside handleTap. audioEngine.start() is also kicked off
    // here (not after handleTap resolves) so its Tone.Transport.start +
    // bedPlayer.start run on the gesture stack.
    void visualEngine.playLoadedVideos();
    const audioStartPromise = audioEngine.start().catch((err) =>
      console.warn('audio start failed', err),
    );

    void session
      .handleTap()
      .then(async () => {
        await audioStartPromise;

        if (!bgLoadStarted) {
          bgLoadStarted = true;
          // Background-preload the videos for every mode beyond mode 0.
          // Single-mode builds skip this entirely. Audio loads on demand from
          // the swipe-driven modeChange handler (AudioEngine.loadMode disposes
          // the active bedPlayer, so we can't preload audio safely).
          for (let i = 1; i < modesFile.modes.length; i++) {
            const idx = i as 0 | 1 | 2;
            void visualEngine
              .loadMode(idx)
              .then(() => visualEngine.playLoadedVideos())
              .catch((err) => console.warn(`preload visual ${idx}`, err));
          }
        }
      })
      .catch((err) => {
        console.error('handleTap failed', err);
        refs.splash.innerHTML = `<h1>audio unavailable</h1><p>${(err as Error).message}</p>`;
      });
  });

  // 8. Resume from INTERRUPTED with any pointerdown.
  document.addEventListener('pointerdown', () => {
    if (session.state === 'INTERRUPTED') void session.handleTap().catch((err) => console.error(err));
  });

  // 9. The per-frame rAF loop (spec §5).
  let prevOutput: MachineMapperOutput | null = null;
  let lastIdleResetAtMs = performance.now();

  // Visuals-only render used before the tap (AWAITING_TAP) and during
  // INTERRUPTED: the video shows through the feedback shader with neutral
  // params and no audio. 0.5 is the shader's no-op center for zoom + rotation.
  const NEUTRAL_VISUAL: VisualParams = {
    feedback: 0,
    zoom: 0.5,
    chroma: 0,
    rotation: 0.5,
    flowX: 0,
    flowY: 0,
    warp: 0,
    blur: 0,
    twist: 0,
  };
  const ZERO_LEVELS: AudioLevels = { bass: 0, mid: 0, treble: 0, rms: 0 };
  let lastAudioLevels: AudioLevels = ZERO_LEVELS;

  function frame(nowMs: number): void {
    let activityThisFrame = false;

    if (session.state === 'PLAYING') {
      const gesture = inputLayer.flush(nowMs);
      const instrumentGateOpen = shouldOpenInstrumentGate(gesture);
      audioEngine.setInstrumentGate(instrumentGateOpen);
      activityThisFrame = gestureHadActivity(gesture);
      if (activityThisFrame) lastIdleResetAtMs = nowMs;
      const idleMs = nowMs - lastIdleResetAtMs;

      const out = machineMapper.tick(gesture, activeMode, idleMs, nowMs, prevOutput);

      audioEngine.apply(out.audio);
      const levels = audioEngine.getLevel();
      lastAudioLevels = levels;
      visualEngine.apply(out.visual, levels, out.touchStamps);
      visualEngine.render();

      for (const ev of out.events) {
        if (ev.kind === 'inject') {
          audioEngine.injectOneshot(ev.index);
        } else if (ev.kind === 'modeChange') {
          // Single-mode builds: ignore swipe-driven mode-changes. MachineMapper
          // still emits them (it doesn't know about mode count) but there's
          // nothing to switch to.
          if (ev.toIndex >= modesFile.modes.length) continue;
          activeModeIndex = ev.toIndex;
          activeMode = modesFile.modes[ev.toIndex];
          visualEngine.setMode(ev.toIndex); // video texture swap — cheap

          // Audio: on-demand load + restart the bed loop. AudioEngine.loadMode
          // disposes the active bedPlayer to make room for the new mode's, so
          // we can't preload other modes in the background — we just pay the
          // load cost on the swipe. After loadMode completes, audioEngine.start
          // is idempotent and will .start() the new bedPlayer.
          void (async () => {
            try {
              await audioEngine.loadMode(ev.toIndex);
              audioEngine.setMode(ev.toIndex);
              await audioEngine.start();
            } catch (err) {
              console.warn('mode-change audio load failed', err);
            }
          })();

          // eslint-disable-next-line no-console
          console.log(`[mode] → ${activeModeIndex} (${activeMode.name})`);
        } else if (ev.kind === 'haptic') {
          fireHaptic(refs, ev.flavor);
        }
      }
      prevOutput = out;
    } else {
      // Pre-tap (AWAITING_TAP) or INTERRUPTED: keep the footage on screen so
      // the experience "starts with the media." Render the video through the
      // feedback shader with neutral params + silence — no gesture mapping, no
      // audio side effects.
      audioEngine.setInstrumentGate(false);
      visualEngine.apply(NEUTRAL_VISUAL, ZERO_LEVELS, []);
      lastAudioLevels = ZERO_LEVELS;
      visualEngine.render();
    }

    session.tick(nowMs, activityThisFrame);

    // Debug HUD — refresh ~4× per second to keep it cheap.
    frameCount++;
    if (hud && nowMs - lastHudAtMs > 250) {
      lastHudAtMs = nowMs;
      // Peek at VisualEngine's videoSources via the public method we added;
      // for raw video state we look at the actual element.
      const peek = (visualEngine as unknown as {
        videoSources?: Map<number, { video: HTMLVideoElement; texture: unknown }>;
      }).videoSources;
      const vs0 = peek?.get(activeModeIndex);
      const v = vs0?.video;
      const audioCtxState = (Tone.getContext().rawContext as AudioContext | undefined)?.state ?? '?';
      // Reach into AudioEngine for diagnostic peeks. Private fields accessed
      // explicitly via a cast — debug-only, never depended on by production paths.
      const ae = audioEngine as unknown as {
        bedPlayer?: {
          loaded?: boolean;
          state?: string;
          playbackRate?: number | { value?: number };
        };
        bank?: { size?: number };
        instrumentGate?: { gain?: { value?: number } };
      };
      const bed = ae.bedPlayer;
      const bedRate = typeof bed?.playbackRate === 'number'
        ? bed.playbackRate
        : bed?.playbackRate?.value;
      const audioGate = ae.instrumentGate?.gain?.value;
      const dest = (Tone.getDestination() as unknown as { volume?: { value?: number } }).volume?.value;
      hud.textContent = [
        `state:      ${session.state}`,
        `mode:       ${activeModeIndex} (${activeMode.name})`,
        `frame:      ${frameCount}`,
        `canvas:     ${refs.canvas.width}×${refs.canvas.height}`,
        `audio.ctx:  ${audioCtxState}`,
        `audio.gate: ${audioGate?.toFixed?.(3) ?? '?'}`,
        `dest.vol:   ${dest === undefined ? '?' : dest.toFixed(1) + ' dB'}`,
        `bed.loaded: ${bed?.loaded ?? '?'}`,
        `bed.state:  ${bed?.state ?? '?'}`,
        `bed.rate:   ${bedRate?.toFixed?.(3) ?? '?'}`,
        `bank.size:  ${ae.bank?.size ?? '?'}`,
        `level.rms:  ${lastAudioLevels.rms.toFixed(3)}`,
        `level.b/m/t:${lastAudioLevels.bass.toFixed(3)} / ${lastAudioLevels.mid.toFixed(3)} / ${lastAudioLevels.treble.toFixed(3)}`,
        `video[${activeModeIndex}]:`,
        `  el?       ${v ? 'yes' : 'NO'}`,
        v && `  src       ${v.currentSrc || v.src}`,
        v && `  readyS    ${v.readyState} (4=HAVE_ENOUGH)`,
        v && `  paused    ${v.paused}`,
        v && `  currT     ${v.currentTime.toFixed(2)}s / ${v.duration.toFixed(2)}s`,
        v && `  size      ${v.videoWidth}×${v.videoHeight}`,
        `prev.feedback: ${prevOutput ? prevOutput.visual.feedback.toFixed(3) : '∅'}`,
        `prev.events:   ${prevOutput ? prevOutput.events.length : '∅'}`,
      ].filter(Boolean).join('\n');
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

boot().catch((err) => {
  console.error('boot failed', err);
  document.body.innerHTML = `<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#000;color:#f88;font-family:system-ui;padding:2rem;text-align:center;"><div><h1>boot failed</h1><pre style="white-space:pre-wrap;font-size:0.8rem;opacity:0.7;">${String((err as Error).message ?? err)}</pre></div></div>`;
});
